// « Colis » view of the ticket window: the carrier tracking (latest events
// first, history on demand), the parcel's identifiers with their origin, its
// values, content and label, the EDN order and the compatibility check.
// Everything comes from GET /api/tickets/{id}/context (shipping_info,
// order_info, compatibility_result) and GET /api/logistics/edn/order_by_id.
// A block renders only when its fields exist.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, MapPin, PackageSearch, Printer } from 'lucide-react';
import { Badge, EmptyState } from '../design/ui';
import PackageIdentifierCards from './PackageIdentifierCards';
import { formatTrackingEventDate, parseUtc } from '../utils/dates';
import { formatMoney, formatShortDateTime } from './TicketWorkspaceHelpers';

const EVENTS_KEEP = 5;

// Amounts of order_info, labelled; any other value the API adds to order_info
// is listed after them (data-driven: a new field is never invisible).
const AMOUNT_FIELDS = [
    ['goods_value', 'messageWindow.goodsValue', 'Valeur des marchandises'],
    ['package_value', 'messageWindow.packageValue', 'Valeur du colis'],
    ['insurance_value', 'messageWindow.insuranceValue', 'Valeur assurée'],
    ['ht_price', 'messageWindow.htPrice', 'Prix HT'],
    ['invoice_total_ht', 'ticketWorkspaceUi.parcel.invoiceTotalHt', 'Total HT de la facture'],
];
const SHOWN_ELSEWHERE = new Set([
    'reference', 'invoice_number', 'order_number', 'tracking_number', 'package_content', 'currency',
    'provenance', 'customer_references', 'reference_conflicts', ...AMOUNT_FIELDS.map(([key]) => key),
]);
const EXTRA_LABELS = {
    return_tracking_number: ['ticketWorkspaceUi.parcel.returnTracking', 'N° de suivi retour'],
};

const hasValue = (value) => value !== null && value !== undefined && value !== '';
const isScalar = (value) => ['string', 'number', 'boolean'].includes(typeof value);
const humanize = (key) => {
    const text = String(key).replace(/_/g, ' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
};

const Section = ({ title, aside, children, className }) => (
    <section className={`tkw-section${className ? ` ${className}` : ''}`}>
        <div className="tkw-section__head">
            <h3 className="tkw-section__title">{title}</h3>
            {aside}
        </div>
        {children}
    </section>
);

const TicketWorkspaceParcel = ({
    loading, shippingInfo, orderInfo, compatibility, serialNumber, ednOrder, ednOrderLoading,
}) => {
    const { t } = useTranslation();
    const [showAllEvents, setShowAllEvents] = useState(false);

    if (loading) {
        return (
            <div className="tkw-side__view tkw-skeleton-stack" aria-busy="true">
                <span className="u-skeleton tkw-skeleton-line tkw-skeleton-line--short" />
                <span className="u-skeleton tkw-skeleton-block" />
                <span className="u-skeleton tkw-skeleton-block" />
            </div>
        );
    }

    const shipping = shippingInfo || {};
    const order = orderInfo || {};
    const tracking = shipping.tracking_info && typeof shipping.tracking_info === 'object' ? shipping.tracking_info : null;
    const events = Array.isArray(tracking?.events) ? tracking.events : [];
    const shownEvents = showAllEvents ? events : events.slice(0, EVENTS_KEEP);
    const hasTracking = Boolean(tracking && Object.keys(tracking).length > 0) || Boolean(shipping.tracking_url);
    const currency = order.currency || '';

    const amounts = AMOUNT_FIELDS
        .filter(([key]) => hasValue(order[key]))
        // invoice_total_ht is read from the same field as ht_price: shown once.
        .filter(([key]) => !(key === 'invoice_total_ht' && hasValue(order.ht_price) && Number(order.ht_price) === Number(order.invoice_total_ht)))
        .map(([key, labelKey, fallback]) => ({ key, label: t(labelKey, fallback), value: formatMoney(order[key], currency) }));
    const extras = Object.entries(order)
        .filter(([key, value]) => !SHOWN_ELSEWHERE.has(key) && hasValue(value) && isScalar(value))
        .map(([key, value]) => ({
            key,
            label: EXTRA_LABELS[key] ? t(EXTRA_LABELS[key][0], EXTRA_LABELS[key][1]) : t(`ticketWorkspaceUi.parcel.fields.${key}`, humanize(key)),
            value: String(value),
        }));
    const identifiers = ['reference', 'order_number', 'invoice_number', 'tracking_number'].some((key) => hasValue(order[key]))
        || (Array.isArray(order.reference_conflicts) && order.reference_conflicts.length > 0);
    const parcelNumber = shipping.tracking_number && shipping.tracking_number !== shipping.master_tracking ? shipping.tracking_number : null;

    const compatibilityParts = Array.isArray(compatibility?.parts) ? compatibility.parts : [];
    const compatibilityMeta = compatibility
        ? Object.entries(compatibility).filter(([key, value]) => key !== 'parts' && hasValue(value))
        : [];
    const partName = (part) => {
        const name = part?.part_name || '';
        const number = part?.part_number || '';
        if (name && number && name.includes(number)) {
            const cleaned = name.replace(number, '').replace(/\s{2,}/g, ' ').trim().replace(/[-–—]+$/, '').trim();
            return cleaned || name;
        }
        return name;
    };
    const formatMeta = (value) => {
        if (typeof value === 'boolean') return value ? t('messageWindow.yes', 'Oui') : t('messageWindow.no', 'Non');
        if (value && typeof value === 'object') {
            try {
                return JSON.stringify(value, null, 2);
            } catch (e) {
                return String(value);
            }
        }
        return String(value);
    };

    const nothing = !hasTracking && !identifiers && amounts.length === 0 && extras.length === 0 && !order.package_content
        && !shipping.label_url && !ednOrder && !ednOrderLoading && !compatibility;
    if (nothing) {
        return (
            <div className="tkw-side__view">
                <EmptyState icon={PackageSearch} title={t('ticketWorkspaceUi.parcel.emptyTitle', 'Aucune information de colis')}>
                    {t('ticketWorkspaceUi.parcel.empty', 'Le suivi, les références et les valeurs apparaissent dès que le ticket en reçoit, du client, d’une intégration ou de l’agent IA.')}
                </EmptyState>
            </div>
        );
    }

    return (
        <div className="tkw-side__view tkw-parcel">
            {hasTracking && (
                <Section
                    className="track-tab"
                    title={t('messageWindow.shippingInfo', "Informations d'expédition")}
                    aside={tracking?.status && (
                        <Badge tone={shipping.is_delivered ? 'success' : 'info'} dot>{tracking.status}</Badge>
                    )}
                >
                    {(shipping.tracking_url || tracking?.fetched_at || parcelNumber) && (
                        <div className="tkw-track__facts">
                            {parcelNumber && (
                                <span className="tkw-ident">
                                    <span className="tkw-ident__label">{t('ticketWorkspaceUi.parcel.parcelNumber', 'Colis')}</span>
                                    <code className="u-code">{parcelNumber}</code>
                                </span>
                            )}
                            {tracking?.fetched_at && (
                                <span className="tracking-refreshed">
                                    {t('messageWindow.trackingRefreshedAt', 'Tracking refreshed on')} {formatShortDateTime(tracking.fetched_at)}
                                    {tracking.source ? ` · ${tracking.source}` : ''}
                                </span>
                            )}
                            {shipping.tracking_url && (
                                <a className="u-link tkw-track__link" href={shipping.tracking_url} target="_blank" rel="noopener noreferrer">
                                    {t('messageWindow.trackingLink', "Track on the carrier's website")}
                                    <ExternalLink strokeWidth={1.8} aria-hidden="true" />
                                </a>
                            )}
                        </div>
                    )}
                    {tracking && events.length === 0 && (
                        <p className="tkw-section__empty">{t('messageWindow.noTrackingEventsYet', 'No tracking events received yet')}</p>
                    )}
                    {events.length > 0 && (
                        <>
                            <ol className="u-timeline tkw-track__events" aria-label={t('messageWindow.trackingHistory', 'Historique de suivi')}>
                                {shownEvents.map((event, index) => (
                                    <li key={index} className="u-timeline__item tracking-event">
                                        <span
                                            className={`u-timeline__dot${index === 0 ? (shipping.is_delivered ? ' u-timeline__dot--success' : ' u-timeline__dot--accent') : ''}`}
                                            aria-hidden="true"
                                        >
                                            {index === 0 && <MapPin strokeWidth={2} />}
                                        </span>
                                        <div className="tkw-track__event">
                                            <div className="u-timeline__title event-status">{event.status}</div>
                                            <div className="u-timeline__meta">
                                                <span className="event-date">{formatTrackingEventDate(event)}</span>
                                                {event.location && <span className="tkw-track__place"> · {event.location}</span>}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                            {events.length > EVENTS_KEEP && (
                                <button
                                    type="button"
                                    className="u-btn u-btn--ghost u-btn--sm tkw-section__more"
                                    onClick={() => setShowAllEvents((value) => !value)}
                                    aria-expanded={showAllEvents}
                                >
                                    {showAllEvents
                                        ? t('ticketWorkspaceUi.parcel.hideHistory', 'Masquer l’historique')
                                        : t('ticketWorkspaceUi.parcel.showHistory', 'Afficher l’historique ({{count}})', { count: events.length - EVENTS_KEEP })}
                                </button>
                            )}
                        </>
                    )}
                </Section>
            )}

            {identifiers && (
                <Section title={t('messageWindow.packageInfo', 'Informations du colis')}>
                    <div className="tkw-facts">
                        <PackageIdentifierCards orderInfo={order} />
                    </div>
                </Section>
            )}

            {(amounts.length > 0 || extras.length > 0 || order.package_content) && (
                <Section title={t('ticketWorkspaceUi.parcel.values', 'Contenu et valeurs')}>
                    {order.package_content && (
                        <p className="tkw-section__text">
                            <span className="u-overline">{t('messageWindow.packageContent', 'Contenu du colis')}</span>
                            <span>{order.package_content}</span>
                        </p>
                    )}
                    {(amounts.length > 0 || extras.length > 0) && (
                        <dl className="tkw-dl tkw-dl--grid">
                            {[...amounts, ...extras].map((item) => (
                                <div key={item.key}>
                                    <dt>{item.label}</dt>
                                    <dd className="u-num">{item.value}</dd>
                                </div>
                            ))}
                        </dl>
                    )}
                </Section>
            )}

            {(shipping.label_url || shipping.shipment_created_at) && (
                <Section title={t('ticketWorkspaceUi.parcel.shipment', 'Expédition')}>
                    <div className="tkw-track__facts">
                        {parseUtc(shipping.shipment_created_at) && (
                            <span>{t('ticketWorkspaceUi.parcel.shipmentCreated', 'Expédition créée le {{date}}', { date: formatShortDateTime(shipping.shipment_created_at) })}</span>
                        )}
                        {shipping.label_url && (
                            <a className="u-btn u-btn--secondary u-btn--sm" href={shipping.label_url} target="_blank" rel="noopener noreferrer">
                                <Printer strokeWidth={1.8} aria-hidden="true" />
                                {t('messageWindow.viewLabel', "Voir l'étiquette")}
                            </a>
                        )}
                    </div>
                </Section>
            )}

            {ednOrderLoading && !ednOrder && (
                <div className="tkw-skeleton-stack" aria-busy="true">
                    <span className="u-skeleton tkw-skeleton-line tkw-skeleton-line--short" />
                    <span className="u-skeleton tkw-skeleton-block" />
                </div>
            )}
            {ednOrder && (
                <Section
                    title={t('ticketWorkspaceUi.parcel.order', 'Commande')}
                    aside={ednOrder.status && <Badge>{ednOrder.status}</Badge>}
                >
                    <dl className="tkw-dl tkw-dl--grid">
                        {[
                            ['reference', t('messageWindow.reference', 'Référence'), ednOrder.reference],
                            ['order_id', t('ticketWorkspaceUi.parcel.orderId', 'N° interne'), ednOrder.order_id],
                            ['total', t('ticketWorkspaceUi.parcel.orderTotal', 'Total'), hasValue(ednOrder.grand_total) ? formatMoney(ednOrder.grand_total, ednOrder.currency) : null],
                            ['shipping', t('ticketWorkspaceUi.parcel.shippingCost', 'Frais de port'), hasValue(ednOrder.shipping_cost) ? formatMoney(ednOrder.shipping_cost, ednOrder.currency) : null],
                            ['created', t('ticketWorkspaceUi.parcel.orderedOn', 'Commandée le'), parseUtc(ednOrder.created_at) ? formatShortDateTime(ednOrder.created_at) : ednOrder.created_at],
                            ['customer', t('ticketWorkspaceUi.parcel.orderCustomer', 'Client'), [ednOrder.customer_name, ednOrder.customer_email, ednOrder.customer_phone].filter(Boolean).join(' · ')],
                        ].filter(([, , value]) => hasValue(value)).map(([key, label, value]) => (
                            <div key={key}>
                                <dt>{label}</dt>
                                <dd>{value}</dd>
                            </div>
                        ))}
                    </dl>
                    {ednOrder.shipping_address && (
                        <p className="tkw-section__text">
                            <span className="u-overline">{t('ticketWorkspaceUi.parcel.shippingAddress', 'Adresse de livraison')}</span>
                            <span>
                                {[
                                    [ednOrder.shipping_address.firstname, ednOrder.shipping_address.lastname].filter(Boolean).join(' '),
                                    ednOrder.shipping_address.street1,
                                    ednOrder.shipping_address.street2,
                                    [ednOrder.shipping_address.postcode, ednOrder.shipping_address.city].filter(Boolean).join(' '),
                                    ednOrder.shipping_address.country,
                                ].filter(Boolean).join(', ')}
                            </span>
                        </p>
                    )}
                    {Array.isArray(ednOrder.items) && ednOrder.items.length > 0 && (
                        <ul className="tkw-lines">
                            {ednOrder.items.map((item, index) => (
                                <li key={item.product_id || item.sku || index} className="tkw-lines__row">
                                    <span className="tkw-lines__main">
                                        <span className="tkw-lines__title">{item.name}</span>
                                        <span className="tkw-lines__meta">
                                            {[item.sku && `SKU ${item.sku}`, item.ean && `EAN ${item.ean}`].filter(Boolean).join(' · ')}
                                        </span>
                                    </span>
                                    <span className="tkw-lines__side u-num">
                                        {item.quantity ? `${item.quantity} × ` : ''}{formatMoney(item.price, ednOrder.currency)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Section>
            )}

            {compatibility && (
                <Section title={t('messageWindow.compatibilityResult', 'Compatibility Result')}>
                    {serialNumber && (
                        <span className="tkw-ident">
                            <span className="tkw-ident__label">{t('messageWindow.serialNumber', 'Serial Number')}</span>
                            <code className="u-code">{serialNumber}</code>
                        </span>
                    )}
                    {compatibilityParts.length > 0 && (
                        <ul className="tkw-lines">
                            {compatibilityParts.map((part, index) => {
                                const name = partName(part) || part.part_number || t('messageWindow.compatibility.partNumber', 'Part Number');
                                const sku = part?.sku || part?.SKU || null;
                                const description = part?.part_desc || part?.partDescription || part?.description;
                                return (
                                    <li key={part.part_number || part.part_name || index} className="tkw-lines__row tkw-lines__row--stack">
                                        <span className="tkw-lines__title">{name}</span>
                                        {description && <span className="tkw-lines__meta">{description}</span>}
                                        <span className="tkw-lines__codes">
                                            {part.part_number && <code className="u-code" title={t('messageWindow.compatibility.partNumber', 'Part Number')}>{part.part_number}</code>}
                                            {sku && <code className="u-code" title={t('messageWindow.compatibility.sku', 'SKU')}>{sku}</code>}
                                            {part.part_group && <span className="u-tag">{part.part_group}</span>}
                                            {part.url && (
                                                <a className="u-link" href={part.url} target="_blank" rel="noopener noreferrer">
                                                    {t('messageWindow.compatibility.viewPart', 'View Part')}
                                                </a>
                                            )}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {compatibilityMeta.length > 0 && (
                        <dl className="tkw-dl">
                            {compatibilityMeta.map(([key, value]) => (
                                <div key={key}>
                                    <dt>{t(`messageWindow.compatibility.${key}`, key.replace(/_/g, ' '))}</dt>
                                    <dd>{value && typeof value === 'object'
                                        ? <pre className="tkw-json">{formatMeta(value)}</pre>
                                        : formatMeta(value)}</dd>
                                </div>
                            ))}
                        </dl>
                    )}
                </Section>
            )}
        </div>
    );
};

export default TicketWorkspaceParcel;
